"use client";

import React from "react";
import { Activity, ArrowRight, CircleCheck, Gauge, Layers, ShieldCheck, SlidersHorizontal } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import InlineCta from "@/components/common/InlineCta";
interface StageData {
  step: string;
  icon: React.ComponentType<{
    className?: string;
    "aria-hidden"?: string | boolean;
  }>;
  title: Record<Lang, string>;
  subtitle: Record<Lang, string>;
  description: Record<Lang, string>;
  standard: string;
  guidance: Record<Lang, string>;
  inputs: Record<Lang, string[]>;
  linkTo?: string;
  linkLabel?: Record<Lang, string>;
}
const STAGES: StageData[] = [{
  step: "01",
  icon: Gauge,
  title: {
    en: "Application & Mechanical Environment Analysis",
    zh: "应用与机械环境工况分析",
    es: "Análisis del entorno mecánico y operativo",
    de: "Analyse der Einsatz- und Umgebungsbedingungen",
    fr: "Analyse des contraintes mécaniques et environnementales",
    pt: "Análise do ambiente mecânico e operacional"
  },
  subtitle: {
    en: "Stress regimes, temperature gradients, and structural boundary conditions",
    zh: "工作应力状态、温度梯度与机械安装边界条件分析",
    es: "Regímenes de esfuerzo, gradientes térmicos y condiciones estructurales",
    de: "Spannungszustände, Temperaturgradienten und Randbedingungen",
    fr: "Régimes de contraintes, gradients thermiques et limites mécaniques",
    pt: "Regimes de esforço, gradientes térmicos e limites estruturais"
  },
  description: {
    en: "Filtration sizing begins by mapping mechanical fatigue thresholds, operational temperature envelopes, and fluid dynamics under transient and steady-state conditions to prevent burst or pleat collapse.",
    zh: "过滤选型必须首先确定机械抗疲劳强度、工作温度区间以及在稳态或瞬态条件下的流体动力学表现，避免滤芯破裂或褶形塌陷。",
    es: "El dimensionamiento comienza definiendo fatiga mecánica, rangos de temperatura y dinámica de fluidos para evitar colapsos bajo presión.",
    de: "Die Auslegung beginnt mit der Erfassung von Dauerfestigkeit, Temperaturbereich und Strömungsdynamik zur Vermeidung von Bersten oder Faltenkollaps.",
    fr: "Le dimensionnement débute par l'évaluation des seuils de fatigue, plages thermiques et dynamique des fluides pour éviter l'écrasement.",
    pt: "O dimensionamento inicia avaliando fadiga mecânica, faixas de temperatura e dinâmica de fluidos para evitar colapso estrutural."
  },
  standard: "ISO 2941 / ASTM F316",
  guidance: {
    en: "Establish maximum differential pressure peaks during cold start and reverse pulse cleaning cycles before specifying mesh wire diameter.",
    zh: "在确定丝网线径前，必须核算低温冷启动与反吹脉冲清洗循环期间的最大瞬时峰值差压。",
    es: "Establezca los picos máximos de presión diferencial durante arranque en frío y ciclos de retrolavado antes de fijar el diámetro del alambre.",
    de: "Ermitteln Sie maximale Differenzdruckspitzen beim Kaltstart und Rückspülzyklen vor der Festlegung des Drahtdurchmessers.",
    fr: "Déterminez les pics de pression différentielle lors des démarrages à froid et rétrolavages avant de spécifier le diamètre de fil.",
    pt: "Calcule os picos máximos de pressão diferencial na partida a frio e ciclos de retrolavagem antes de definir o diâmetro do fio."
  },
  inputs: {
    en: ["Continuous & peak operating temperature range", "Maximum allowable differential pressure (ΔP collapse rating)", "Flow regime characterization (laminar, turbulent, reverse pulse)", "Mechanical housing constraints & perimeter edge sealing method"],
    zh: ["连续与峰值工作温度范围", "最大允许工作压差 (ΔP 抗爆破溃缩等级)", "流体工况流态判定 (层流、湍流或反冲洗脉冲)", "机械滤壳配合尺寸及边缘密封或包边形式"],
    es: ["Rango de temperatura continua y pico", "Presión diferencial máxima admisible (resistencia al colapso ΔP)", "Régimen de flujo (laminar, turbulento, pulso inverso)", "Dimensiones de carcasa y método de sellado perimetral"],
    de: ["Dauer- und Spitzenbetriebstemperaturbereich", "Maximal zulässiger Differenzdruck (ΔP-Kollapsgrenze)", "Strömungscharakteristik (laminar, turbulent, Rückspülimpuls)", "Gehäuseabmessungen und Dichtungskonzept"],
    fr: ["Plage de température continue et de crête", "Pression différentielle maximale admissible (tenue au colmatage ΔP)", "Régime d'écoulement (laminaire, turbulent, rétrolavage)", "Contraintes de boîtier et système d'étanchéité périphérique"],
    pt: ["Faixa de temperatura contínua e de pico", "Pressão diferencial máxima admissível (resistência ao colapso ΔP)", "Regime de fluxo (laminar, turbulento, pulso reverso)", "Restrições da carcaça e método de vedação perimetral"]
  }
}, {
  step: "02",
  icon: SlidersHorizontal,
  title: {
    en: "Particle Retention & Aperture Geometry",
    zh: "颗粒截留与网孔几何构型",
    es: "Retención de partículas y geometría de abertura",
    de: "Partikelrückhalt & Maschengeometrie",
    fr: "Rétention particulaire et géométrie d'ouverture",
    pt: "Retenção de partículas e geometria de abertura"
  },
  subtitle: {
    en: "Micron cutoff calibration, open area balance, and pore consistency",
    zh: "微米级过滤精度标定、开孔率平衡与孔径均匀度",
    es: "Calibración del corte en micras, balance de área abierta y consistencia de poro",
    de: "Mikron-Kalibrierung, offene Siebfläche und Porenkonstanz",
    fr: "Seuil micronique, équilibre de surface ouverte et régularité des pores",
    pt: "Calibração de corte em mícrons, balanço de área aberta e consistência de poro"
  },
  description: {
    en: "Determining absolute and nominal particle cutoff thresholds based on contaminant morphology, aspect ratios, and target separation efficiency across the pore geometry spectrum.",
    zh: "根据杂质颗粒形态、长径比以及目标分离效率，在完整的网孔几何谱系中精确标定绝对截留与公称截留精度。",
    es: "Determinación de umbrales absolutos y nominales según morfología de contaminantes y eficiencia de separación requerida.",
    de: "Festlegung absoluter und nomineller Abscheidegrade anhand von Partikelmorphologie und geforderter Abscheideleistung.",
    fr: "Détermination des seuils absolus et nominaux selon la morphologie des particules et l'efficacité de séparation visée.",
    pt: "Determinação de limites absolutos e nominais conforme morfologia do contaminante e eficiência de separação."
  },
  standard: "ASTM E11 / ISO 9044",
  guidance: {
    en: "Balancing target micron cut-off with open area percentage (Fo %) prevents premature face blinding and maintains acceptable clean pressure loss.",
    zh: "在目标微米精度与有效开孔率 (Fo %) 之间取得最佳平衡，以防止介质表面早期堵塞并维持可控的初始洁净阻力。",
    es: "Equilibrar el corte en micras con el área abierta previene colmatación prematura y mantiene bajas pérdidas de carga iniciales.",
    de: "Die Abstimmung von Mikron-Trenngrenze und offener Siebfläche verhindert vorzeitiges Zusetzen und sichert geringen Anfangsdruckverlust.",
    fr: "Équilibrer seuil micronique et surface ouverte évite l'encrassement prématuré et préserve une faible perte de charge propre.",
    pt: "Equilibrar a abertura em mícrons com a porcentagem de área aberta evita bloqueio prematuro da superfície filtrante."
  },
  inputs: {
    en: ["Target particle cut-off rating (1 µm to 25 mm range)", "Particulate classification (hard, crystalline, gelatinous, fibers)", "Calculated open area percentage (Fo %) for required volumetric flux", "Aperture uniformity tolerance class (Standard or High Precision)"],
    zh: ["目标颗粒截留精度 (1 µm 至 25 mm 区间)", "杂质微粒类型 (硬质颗粒、结晶物、凝胶胶体、纤维)", "满足通量要求所需的计算有效开孔率 (Fo %)", "网孔均匀度公差等级 (标准工业级或精密公差级)"],
    es: ["Clasificación de corte objetivo (rango 1 µm a 25 mm)", "Tipo de partícula (dura, cristalina, gelatinosa, fibras)", "Porcentaje de área abierta calculado para el flujo requerido", "Tolerancia de abertura (estándar o alta precisión)"],
    de: ["Ziel-Abscheidegrad (Bereich 1 µm bis 25 mm)", "Partikelklassifizierung (hart, kristallin, gelatinös, faserig)", "Berechnete offene Siebfläche für geforderten Durchsatz", "Toleranzklasse der Maschenweite (Standard oder Präzision)"],
    fr: ["Seuil de coupure cible (plage 1 µm à 25 mm)", "Classification particulaire (dure, cristalline, gélatineuse, fibreuse)", "Surface ouverte calculée pour le débit requis", "Classe de tolérance d'ouverture (standard ou haute précision)"],
    pt: ["Classificação de corte alvo (faixa de 1 µm a 25 mm)", "Classificação particulada (dura, cristalina, gelatinosa, fibras)", "Área aberta calculada para a vazão volumétrica requerida", "Classe de tolerância de abertura (padrão ou alta precisão)"]
  },
  linkTo: "/ApertureMicronRatingChartsPage",
  linkLabel: {
    en: "View Aperture & Micron Charts",
    zh: "查看孔径与微米对照表",
    es: "Ver tablas de abertura y micras",
    de: "Maschenweiten- und Mikrontabellen anzeigen",
    fr: "Consulter les tableaux d'ouvertures et microns",
    pt: "Ver tabelas de abertura e mícrons"
  }
}, {
  step: "03",
  icon: ShieldCheck,
  title: {
    en: "Alloy & Metallurgical Compatibility",
    zh: "合金与冶金耐腐蚀选型",
    es: "Compatibilidad metalúrgica y selección de aleaciones",
    de: "Werkstoff- & Legierungsverträglichkeit",
    fr: "Compatibilité métallurgique et choix d'alliage",
    pt: "Compatibilidade metalúrgica e seleção de ligas"
  },
  subtitle: {
    en: "Chemical resistance matrices, PREN scores, and thermal degradation limits",
    zh: "化学耐腐蚀矩阵、PREN 耐点蚀指数与热退化极限",
    es: "Matrices de resistencia química, índice PREN y límites térmicos",
    de: "Chemische Beständigkeit, PREN-Werte und thermische Einsatzgrenzen",
    fr: "Matrices de résistance chimique, indices PREN et limites thermiques",
    pt: "Matrizes de resistência química, índices PREN e limites térmicos"
  },
  description: {
    en: "Selection of the stainless steel or specialty alloy chemistry based on halide concentrations, pH levels, operating atmosphere, and resistance to intergranular attack.",
    zh: "基于流体卤素离子浓度、酸碱 pH 值、工作环境介质以及晶间腐蚀倾向，评估并推荐适宜的不锈钢或特种耐蚀合金牌号。",
    es: "Selección de aleación según concentración de haluros, pH y resistencia a corrosión intergranular.",
    de: "Auswahl der Edelstahllegierung anhand von Halogenkonzentrationen, pH-Werten und Beständigkeit gegen interkristalline Korrosion.",
    fr: "Sélection d'alliage selon la teneur en halogénures, pH et résistance à la corrosion intergranulaire.",
    pt: "Seleção da liga com base na concentração de haletos, pH e resistência à corrosão intergranular."
  },
  standard: "ASTM A580 / DIN EN 10088-3",
  guidance: {
    en: "High-chloride environments with elevated temperatures mandate alloys with elevated PREN (such as AISI 316L or specialty grades) to avoid stress corrosion cracking.",
    zh: "在介质温度升高的高氯离子环境中，建议采用高 PREN 的合金 (如 AISI 316L 或高耐蚀特种牌号)，避免晶间应力腐蚀开裂。",
    es: "Entornos con cloruros a temperaturas elevadas requieren aleaciones con mayor PREN (como AISI 316L o grados especiales).",
    de: "Chloridhaltige Umgebungen mit erhöhten Temperaturen erfordern Legierungen mit höherem PREN-Wert (wie AISI 316L oder Sonderlegierungen).",
    fr: "Les environnements chlorés à températures élevées imposent des alliages à PREN élevé (comme AISI 316L ou nuances spéciales).",
    pt: "Ambientes com cloretos em temperaturas elevadas exigem ligas com maior PREN (como AISI 316L ou graus especiais)."
  },
  inputs: {
    en: ["Process fluid chemistry, halogen/chloride concentration & pH", "Pitting Resistance Equivalent Number (PREN) requirements", "Intergranular corrosion stabilization (e.g. 304L, 316L)", "Specialty alloy evaluation for severe aggressive streams"],
    zh: ["工艺流体化学组分、卤素/氯离子含量及工作 pH 值", "耐点蚀当量指数 (PREN) 门槛要求", "抗晶间腐蚀稳定化要求 (如 304L、316L)", "严苛腐蚀工况特种合金技术评估"],
    es: ["Química del fluido de proceso, haluros/cloruros y pH", "Requisitos de índice PREN (resistencia a picaduras)", "Estabilización contra corrosión intergranular (p. ej. 304L, 316L)", "Evaluación de aleaciones especiales para medios severos"],
    de: ["Prozessfluidchemie, Halogen-/Chloridgehalt und pH-Wert", "Anforderungen an die Lochfraßbeständigkeit (PREN)", "Stabilisierung gegen interkristalline Korrosion (z. B. 304L, 316L)", "Prüfung von Sonderlegierungen für hochaggressive Medien"],
    fr: ["Chimie du fluide, teneur en chlorures et pH", "Exigences de résistance aux piqûres (PREN)", "Stabilisation contre la corrosion intergranulaire (ex. 304L, 316L)", "Évaluation d'alliages spéciaux pour flux très agressifs"],
    pt: ["Química do fluido de processo, haletos/cloretos e pH", "Requisitos de índice PREN (resistência a pites)", "Estabilização contra corrosão intergranular (ex. 304L, 316L)", "Avaliação de ligas especiais para fluidos severos"]
  },
  linkTo: "/CorrosionResistanceMatricesPage",
  linkLabel: {
    en: "Inspect Corrosion Resistance Matrices",
    zh: "查阅耐腐蚀性选型矩阵",
    es: "Ver matrices de resistencia a la corrosión",
    de: "Korrosionsbeständigkeitsmatrizen prüfen",
    fr: "Consulter les matrices de résistance à la corrosion",
    pt: "Ver matrizes de resistência à corrosão"
  }
}, {
  step: "04",
  icon: Layers,
  title: {
    en: "Weave Type Selection",
    zh: "编织构型与结构选择",
    es: "Selección del tipo de tejido y estructura",
    de: "Auswahl der Web- und Verbundstruktur",
    fr: "Sélection du type d'armure et de structure",
    pt: "Seleção do tipo de tecelagem e estrutura"
  },
  subtitle: {
    en: "Mechanical stability, pore tortuosity, and surface versus depth characteristics",
    zh: "力学结构稳定性、曲折孔道路径以及表层与深层过滤特性",
    es: "Estabilidad mecánica, tortuosidad de poro y características de superficie vs profundidad",
    de: "Stabilität, Porentortuosität und Oberflächen- vs. Tiefenfiltration",
    fr: "Stabilité mécanique, tortuosité des pores et filtration de surface vs profondeur",
    pt: "Estabilidade mecânica, tortuosidade de poro e filtração de superfície vs profundidade"
  },
  description: {
    en: "Aligning hydrodynamic requirements with square weave, twilled Dutch weave, or multi-layer diffusion-bonded sintered wire mesh to achieve the exact flow resistance and backwash recovery.",
    zh: "根据水力学要求与机械刚度，选配平纹方孔网、密纹荷兰编织网或多层烧结复合网，达到精确的阻力曲线与反洗可再生性。",
    es: "Alineación de requisitos hidrodinámicos con malla cuadrada, tejido Dutch o malla sinterizada multicapa.",
    de: "Abstimmung strömungstechnischer Anforderungen mit Quadratmaschen, Tressengewebe oder mehrlagigem Sintermetall.",
    fr: "Harmonisation des exigences hydrauliques avec toiles carrées, toiles reps ou multicouches frittées.",
    pt: "Alinhamento das exigências hidrodinâmicas com malha quadrada, trama Dutch ou malha sinterizada multicamada."
  },
  standard: "DIN ISO 4783-2",
  guidance: {
    en: "Dutch weaves provide high burst strength and tortuous filtration paths for fines, while plain square weaves ensure maximum open area and surface-cake release.",
    zh: "荷兰密纹网提供高破裂承载力与曲折过滤通道，而方孔平纹网则具有最大的透孔通量与滤饼自脱落剥离性能。",
    es: "Los tejidos Dutch proporcionan alta resistencia mecánica y canales tortuosos; las mallas cuadradas maximizan área abierta.",
    de: "Tressengewebe bieten hohe Festigkeit und verwinkelte Poren; Quadratmaschen maximieren offene Fläche und Kuchenabwurf.",
    fr: "Les toiles reps offrent une haute résistance à l'éclatement; les toiles carrées maximisent la surface ouverte.",
    pt: "Tramas Dutch oferecem alta resistência à ruptura; malhas quadradas maximizam a área aberta."
  },
  inputs: {
    en: ["Plain square weave for rapid surface separation & sifting", "Twilled weave for heavy wire gauges at compact apertures", "Plain Dutch & Twill Dutch weaves for high-pressure streams", "Reverse Dutch or sintered multi-layer laminate for rigid high-velocity flows"],
    zh: ["平纹方孔编织网：用于快速表层筛分与固体拦截", "斜纹编织网：在紧密网孔下实现粗线径高刚性制造", "平纹/斜纹荷兰密纹网：高压工况与精细固液分离", "反向荷兰网或多层真空烧结网：抗高流速冲刷的高强刚性构型"],
    es: ["Malla cuadrada lisa para separación superficial rápida y tamizado", "Tejido sarga para alambres más gruesos con aberturas compactas", "Tejidos Dutch liso y sarga para corrientes de alta presión", "Dutch inverso o laminado sinterizado para flujos rígidos a alta velocidad"],
    de: ["Glatte Quadratmaschen für schnelle Oberflächensiebung", "Köperbindung für dickere Drähte bei feinen Maschenweiten", "Glatte und Köpertressengewebe für Hochdruckanwendungen", "Panzertresse oder Sinterlaminate für strömungsbelastete Medien"],
    fr: ["Toile unie carrée pour séparation de surface rapide et criblage", "Tissage sergé pour fils plus épais à ouvertures fines", "Toiles reps unies et sergées pour flux haute pression", "Reps inversé ou laminé fritté pour débits à haute vélocité"],
    pt: ["Malha quadrada lisa para separação rápida de superfície e peneiramento", "Trama sarja para arames mais grossos em aberturas compactas", "Tramas Dutch lisa e sarja para fluxos de alta pressão", "Dutch reversa ou laminado sinterizado para vazões de alta velocidade"]
  },
  linkTo: "/ProductsPage",
  linkLabel: {
    en: "Explore Mesh Product Catalog",
    zh: "浏览不锈钢丝网产品系列",
    es: "Explorar catálogo de productos",
    de: "Produktkatalog Drahtgewebe ansehen",
    fr: "Explorer le catalogue de toiles métalliques",
    pt: "Explorar catálogo de produtos"
  }
}, {
  step: "05",
  icon: Activity,
  title: {
    en: "Flow Rate & Differential Pressure Calculations",
    zh: "流量与差压计算验证",
    es: "Cálculos de caudal y presión diferencial",
    de: "Durchfluss- & Differenzdruckberechnungen",
    fr: "Calculs de débit et perte de charge",
    pt: "Cálculos de vazão e pressão diferencial"
  },
  subtitle: {
    en: "Clean ΔP0 modeling, Darcy-Forchheimer coefficients, and dirt accumulation models",
    zh: "初始洁净 ΔP0 建模、Darcy-Forchheimer 渗流系数与容污纳污分析",
    es: "Modelado de ΔP inicial, coeficientes de Darcy y acumulación de suciedad",
    de: "Modellierung des Anfangsdruckverlusts ΔP, Darcy-Kennwerte und Beladungsverlauf",
    fr: "Modélisation de ΔP initial, coefficients de Darcy et colmatage",
    pt: "Modelagem de ΔP inicial, coeficientes de Darcy e acúmulo de sujidade"
  },
  description: {
    en: "Analytical verification combining Darcy permeability coefficients, fluid dynamic viscosity, and volumetric flow rates to model clean drop and project filter element service life.",
    zh: "结合达西渗透系数、流体动力粘度与体积流量，进行初始压降理论计算与滤芯工况使用寿命综合预测建模。",
    es: "Verificación analítica combinando permeabilidad de Darcy, viscosidad y caudal para proyectar vida útil.",
    de: "Analytische Verifikation mit Darcy-Permeabilität, Viskosität und Volumenstrom zur Abschätzung der Standzeit.",
    fr: "Vérification combinant perméabilité de Darcy, viscosité et débits pour projeter la durée de service.",
    pt: "Verificação combinando coeficientes de Darcy, viscosidade e vazão volumétrica para projetar vida útil."
  },
  standard: "ISO 3968 / Modified Darcy Flow",
  guidance: {
    en: "Clean pressure drop (ΔP0) should not exceed 10% to 15% of the total available line pressure drop to allow sufficient headroom for contaminant cake formation.",
    zh: "初始洁净状态压降 (ΔP0) 通常应控制在总允许压差预算的 10%–15% 以内，以预留充足的滤饼纳污阻力上升空间。",
    es: "La caída de presión inicial (ΔP0) no debe superar el 10% al 15% del diferencial disponible.",
    de: "Der Anfangsdruckverlust (ΔP0) sollte 10% bis 15% des zulässigen Gesamtdruckverlusts nicht überschreiten.",
    fr: "La perte de charge initiale (ΔP0) ne devrait pas dépasser 10% à 15% de la pression admissible.",
    pt: "A queda de pressão inicial (ΔP0) não deve exceder 10% a 15% do diferencial total disponível."
  },
  inputs: {
    en: ["Fluid kinematic & dynamic viscosity at operating temperatures", "Calculated clean differential pressure loss (ΔP0) via modified Darcy models", "Estimated dirt-holding capacity before terminal ΔP threshold is reached", "Regeneration cycle frequency (backflush duration vs continuous operation)"],
    zh: ["工作温度下的流体动力粘度与运动粘度", "基于修正达西模型的初始洁净压降损失 (ΔP0) 理论计算", "达到最终报警极限压差前的有效容污截留量估算", "在线反洗再生频率与连续不间断过滤生命周期评估"],
    es: ["Viscosidad cinemática y dinámica a temperatura de trabajo", "Pérdida de carga limpia inicial (ΔP0) mediante modelos de Darcy", "Capacidad de retención de suciedad hasta el límite de ΔP", "Frecuencia de retrolavado o funcionamiento continuo"],
    de: ["Kinematische und dynamische Viskosität bei Betriebstemperatur", "Berechneter Anfangsdruckverlust (ΔP0) nach Darcy-Modellen", "Geschätzte Schmutzaufnahmekapazität bis zum Enddifferenzdruck", "Rückspülhäufigkeit bzw. kontinuierlicher Betrieb"],
    fr: ["Viscosité cinématique et dynamique aux températures de service", "Perte de charge initiale propre (ΔP0) selon modèle de Darcy", "Capacité de rétention des impuretés jusqu'au seuil terminal", "Fréquence des cycles de régénération"],
    pt: ["Viscosidade cinemática e dinâmica nas temperaturas de operação", "Queda de pressão inicial (ΔP0) via modelos de Darcy", "Capacidade estimada de retenção até a ΔP terminal", "Frequência de regeneração por retrolavagem"]
  },
  linkTo: "/DifferentialPressureDeltaPModelsPage",
  linkLabel: {
    en: "Calculate Differential Pressure ΔP",
    zh: "进入差压 ΔP 模型计算",
    es: "Calcular presión diferencial ΔP",
    de: "Differenzdruck ΔP berechnen",
    fr: "Calculer la perte de charge ΔP",
    pt: "Calcular pressão diferencial ΔP"
  }
}];
const METHODOLOGY_COPY = {
  badge: {
    en: "Engineering Sizing Methodology",
    zh: "工程选型计算规范",
    es: "Metodología de dimensionamiento técnico",
    de: "Methodik zur technischen Gewebeauslegung",
    fr: "Méthodologie de dimensionnement technique",
    pt: "Metodologia de dimensionamento técnico"
  },
  heading: {
    en: "5-Stage Metallurgical & Geometric Filter Sizing Framework",
    zh: "五阶段冶金与几何过滤选型计算框架",
    es: "Marco de dimensionamiento metalúrgico y geométrico en 5 etapas",
    de: "5-Stufen-Framework zur metallurgischen und geometrischen Auslegung",
    fr: "Cadre de dimensionnement métallurgique et géométrique en 5 étapes",
    pt: "Estrutura de dimensionamento metalúrgico e geométrico em 5 etapas"
  },
  lead: {
    en: "A structured, five-stage analytical process for engineers and procurement specialists to calculate aperture geometry, verify metallurgical limits, and predict flow performance before tooling.",
    zh: "严格遵循国际流体动力学与筛网制造标准，通过多维度工况推导、网孔流阻建模与冶金抗蚀校验，为严苛工业过滤环境提供高可靠性配置方案。",
    es: "Un proceso analítico estructurado en cinco etapas para calcular geometría de abertura, límites metalúrgicos y flujo.",
    de: "Ein fünfstufiger Analyseprozess zur Berechnung von Maschengeometrie, Werkstoffgrenzen und Strömungsverhalten.",
    fr: "Un processus analytique en cinq étapes pour calculer la géométrie des mailles et vérifier les limites métallurgiques.",
    pt: "Um processo estruturado em cinco etapas para calcular geometria de abertura, limites metalúrgicos e desempenho de vazão."
  },
  technicalGuidance: {
    en: "Technical Guidance",
    zh: "技术实施建议",
    es: "Orientación técnica",
    de: "Technische Hinweise",
    fr: "Recommandation technique",
    pt: "Recomendação técnica"
  },
  keyInputs: {
    en: "Key Engineering Inputs",
    zh: "关键工程输入参数",
    es: "Parámetros técnicos clave",
    de: "Wesentliche Auslegungsparameter",
    fr: "Paramètres clés d'ingénierie",
    pt: "Parâmetros técnicos principais"
  },
  inlineCtaText: {
    en: "Need custom mesh calculations, pore geometry sizing, or alloy validation for your application?",
    zh: "需要为您的系统定制网孔工程计算、孔隙几何选型或合金材质验证？",
    es: "¿Necesita cálculos de malla a medida, dimensionamiento de poros o validación de aleaciones?",
    de: "Benötigen Sie individuelle Gewebeberechnungen, Porengeometrie-Auslegungen oder Werkstoffprüfungen?",
    fr: "Besoin de calculs sur mesure, dimensionnement de pores ou validation d'alliage pour votre installation ?",
    pt: "Precisa de cálculos sob medida, dimensionamento de poros ou validação de ligas para o seu sistema?"
  },
  inlineCtaButton: {
    en: "Consult Engineering",
    zh: "咨询技术工程师",
    es: "Consultar a ingeniería",
    de: "Ingenieurteam kontaktieren",
    fr: "Consulter l'ingénierie",
    pt: "Consultar engenharia"
  }
};
export default function FilterSizingMethodology() {
  const {
    lang
  } = useLanguage();
  return <section id="filtersizingmethodology" data-nav="light" className="bg-background text-foreground mf-section relative overflow-hidden">
      <div className="mf-container relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mb-16 lg:mb-20">
          <Badge variant="outline" className="mb-4 font-mono mf-caption uppercase tracking-wider border-border bg-card">
            {pick(lang, METHODOLOGY_COPY.badge)}
          </Badge>
          <h2 className="mf-h2 font-semibold tracking-tight text-foreground mb-5 text-balance">
            {pick(lang, METHODOLOGY_COPY.heading)}
          </h2>
          <p className="mf-lead text-muted-foreground leading-relaxed">
            {pick(lang, METHODOLOGY_COPY.lead)}
          </p>
        </div>

        {/* Stepper Timeline */}
        <div className="relative">
          {/* Vertical Connecting Line (Desktop) */}
          <div className="hidden lg:block absolute left-[3.25rem] top-8 bottom-8 w-0.5 bg-border pointer-events-none" aria-hidden="true" />

          <div className="space-y-10 sm:space-y-12">
            {STAGES.map((stage, idx) => {
            const IconComp = stage.icon;
            return <div key={stage.step} data-index={idx} className="relative grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
                  {/* Step Marker / Left Column */}
                  <div className="lg:col-span-2 flex items-center lg:items-start gap-4 lg:flex-col lg:pl-4">
                    <div className="relative z-10 size-14 rounded-lg bg-card border border-border flex items-center justify-center text-primary shrink-0">
                      <span className="font-mono text-lg font-bold">
                        {stage.step}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="mf-caption font-mono uppercase tracking-wider text-muted-foreground">
                        {lang === "zh" ? `阶段 ${stage.step}` : `STAGE ${stage.step}`}
                      </span>
                      <div className="flex items-center gap-1.5 mf-caption text-foreground font-medium mt-0.5">
                        <IconComp className="size-3.5 text-primary" aria-hidden="true" />
                        <span>{stage.standard.split(" / ")[0]}</span>
                      </div>
                    </div>
                  </div>

                  {/* Main Content Card / Right Column */}
                  <div className="lg:col-span-10">
                    <Card className="border-border rounded-lg hover:border-primary/40 transition-colors">
                      <CardHeader className="pb-4">
                        <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                          <CardTitle className="mf-h3 font-semibold tracking-tight text-foreground">
                            {pick(lang, stage.title)}
                          </CardTitle>
                          <Badge variant="outline" className="font-mono mf-caption px-2.5 py-1 text-foreground bg-muted border-border">
                            {stage.standard}
                          </Badge>
                        </div>
                        <p className="text-sm font-medium text-muted-foreground">
                          {pick(lang, stage.subtitle)}
                        </p>
                      </CardHeader>

                      <CardContent className="space-y-6 pt-0">
                        <p className="text-sm sm:text-base text-foreground leading-relaxed">
                          {pick(lang, stage.description)}
                        </p>

                        {/* Technical Guidance Callout Box */}
                        <div className="rounded-lg bg-muted/60 border border-border p-4 sm:p-5">
                          <div className="flex items-center gap-2 mb-2 mf-caption font-semibold uppercase tracking-wider text-primary">
                            <CircleCheck className="size-4" aria-hidden="true" />
                            <span>
                              {pick(lang, METHODOLOGY_COPY.technicalGuidance)}
                            </span>
                          </div>
                          <p className="text-sm text-foreground/80 leading-relaxed">
                            {pick(lang, stage.guidance)}
                          </p>
                        </div>

                        {/* Key Engineering Inputs */}
                        <div>
                          <h3 className="mf-caption font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                            {pick(lang, METHODOLOGY_COPY.keyInputs)}
                          </h3>
                          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                            {pick(lang, stage.inputs).map((input, iIndex) => <li key={iIndex} data-index={iIndex} className="flex items-start gap-2.5 mf-caption sm:text-sm text-foreground/90">
                                  <span className="size-1.5 rounded-full bg-primary mt-2 shrink-0" aria-hidden="true" />
                                  <span>{input}</span>
                                </li>)}
                          </ul>
                        </div>

                        {/* Action Link if provided */}
                        {stage.linkTo && stage.linkLabel && <div className="pt-2 flex items-center justify-between">
                            <Button asChild variant="outline" size="sm" className="group mf-caption font-medium">
                              <Link to={stage.linkTo}>
                                <span>
                                  {pick(lang, stage.linkLabel)}
                                </span>
                                <ArrowRight className="size-3.5 text-muted-foreground group-hover:text-primary transition-colors" aria-hidden="true" />
                              </Link>
                            </Button>
                          </div>}
                      </CardContent>
                    </Card>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Mid-page quote / consultation prompt: exactly ONE as InlineCta per batch rules */}
        <div className="mt-16 lg:mt-20">
          <InlineCta text={pick(lang, METHODOLOGY_COPY.inlineCtaText)} buttonLabel={pick(lang, METHODOLOGY_COPY.inlineCtaButton)} to="/ContactPage" />
        </div>
      </div>
    </section>;
}